"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useAuthStore } from "@/stores/auth-store";
import { noteSignedIn, signInHref } from "@/lib/login-next";

interface AuthGuardProps {
  children: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const t = useTranslations("common");
  const router = useRouter();
  const { isAuthenticated, isLoading, fetchUser, token, _hasHydrated, authUnreachable } = useAuthStore();
  const [isChecking, setIsChecking] = useState(true);
  // A visitor who ARRIVED signed out (a push tap, a shared link) is sent back
  // to that page after sign-in. Once this page load has a signed-in user, a
  // sign-out or an expired session goes to a plain /login instead, so the
  // next person on a shared device starts from home (lib/login-next.ts).
  useEffect(() => {
    if (isAuthenticated) noteSignedIn();
  }, [isAuthenticated]);

  useEffect(() => {
    // Wait for Zustand to hydrate from localStorage before checking auth
    if (!_hasHydrated) {
      return;
    }

    const checkAuth = async () => {
      // Check if we have a token (from hydrated state or localStorage)
      const storedToken = token || localStorage.getItem('token');

      if (storedToken && !isAuthenticated) {
        await fetchUser();
      }
      setIsChecking(false);
    };

    checkAuth();
  }, [_hasHydrated, token, isAuthenticated, fetchUser]);

  useEffect(() => {
    if (!_hasHydrated) {
      return;
    }

    // Server unreachable with a token in hand: stay signed in, offer Retry.
    if (!isChecking && !isLoading && !isAuthenticated && !authUnreachable) {
      router.push(signInHref(window.location.pathname + window.location.search));
    }
  }, [_hasHydrated, isChecking, isLoading, isAuthenticated, authUnreachable, router]);

  // Show loading while waiting for hydration or auth check
  if (!_hasHydrated || isChecking || isLoading) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">{t("authGuard.loading")}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated && authUnreachable) {
    return (
      <div className="flex min-h-svh items-center justify-center p-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-muted-foreground">{t("authGuard.unreachable")}</p>
          <button
            type="button"
            onClick={() => void fetchUser()}
            className="border px-4 py-2 text-sm font-medium"
          >
            {t("authGuard.retry")}
          </button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}
